import React, { Dispatch, SetStateAction, createContext, useContext, useEffect, useRef, useState } from 'react'
import axios from 'axios'
import { User } from '../types'
import { useAxiosInstance } from '../api/AxiosInstanceContext'
import { WaiverContext } from './waiversContext';
import { AuthContext } from './authContext'
import { UserContext } from './userContext'
import { CurrentCohortSubscriptionContext } from './currentCohortSubscriptionContext'

export function clientErrorMessage(error: unknown, fallback: string): string {
  if (axios.isAxiosError<{ message?: string }>(error) && typeof error.response?.data?.message === 'string') {
    return error.response.data.message
  }
  return fallback
}

export type ClientRemovalResult = {
  action: 'archive' | 'blocked' | 'disabled';
  message: string;
  reasons?: string[];
  case_count?: number;
}

interface ClientsContext {
  scopeKey: string;
  cohortId: number | undefined;
  clients: User[];
  archivedClients: User[];
  clientsError: string | null
  archivedClientsError: string | null;
  clientsLoading: boolean;
  archivedClientsLoading: boolean;
  addClient: ({
    untracked,
    email,
    firstName,
    lastName,
    ageRange,
    phoneNumber,
    possiblyPregnant,
    hbpDiagnosed
  }: {
    untracked: boolean
    email: string
    firstName: string
    lastName: string
    ageRange: string;
    phoneNumber: string;
    possiblyPregnant: string;
    hbpDiagnosed: string;
  }) => Promise<void>
  addClientError: string | null
  setAddClientError: Dispatch<SetStateAction<string | null>>
  removeClientError: string | null
  setRemoveClientError: Dispatch<SetStateAction<string | null>>
  getClient: (clientId: User | number) => User | undefined;
  refreshClients: () => Promise<void>;
  refreshArchivedClients: () => Promise<void>;
  resendWaiver: (caseId: number) => Promise<boolean>;
  getClientRemovalPreview: (clientId: number, cohortId?: number) => Promise<ClientRemovalResult>;
  removeClient: (clientId: number, cohortId?: number) => Promise<ClientRemovalResult>;
  restoreClient: (clientId: number, cohortId?: number) => Promise<void>;
}

export const ClientsContext = createContext<ClientsContext>(undefined as any)

interface Props {
  children: React.ReactNode
}

export const ClientsProvider: React.FC<Props> = ({ children }) => {
  const axiosInstance = useAxiosInstance();
  const { waiverResults } = useContext(WaiverContext);
  const { currentUser } = useContext(AuthContext);
  const { user } = useContext(UserContext);
  const { currentCohortSubscription } = useContext(CurrentCohortSubscriptionContext);
  const cohortId = currentCohortSubscription?.cohort.id;
  const enabled = user.features?.heal_cohort_clients ?? false;
  const scopeKey = `${currentUser?.uid || ''}:${cohortId || ''}:${enabled}`;
  const scopeRef = useRef({ key: scopeKey });
  if (scopeRef.current.key !== scopeKey) scopeRef.current = { key: scopeKey };
  const scope = scopeRef.current;
  const mounted = useRef(true);
  const requests = useRef({ active: 0, archived: 0 });
  const controllers = useRef<{ active?: AbortController; archived?: AbortController }>({});
  const emptyList = () => ({ data: [] as User[], error: null as string | null, loading: false });
  const [lists, setLists] = useState({ scope, active: emptyList(), archived: emptyList() });
  const visible = lists.scope === scope ? lists : { scope, active: emptyList(), archived: emptyList() };
  const [addClientError, setAddClientError] = useState<string | null>(null);
  const [removeClientError, setRemoveClientError] = useState<string | null>(null);

  const isCurrent = () => mounted.current && scopeRef.current === scope;
  const requestConfig = (expectedCohortId = cohortId) => {
    if (!isCurrent() || !currentUser || !cohortId || expectedCohortId !== cohortId) {
      throw new Error('Client selection is no longer current.');
    }
    return { headers: { 'X-Cohort-Id': String(expectedCohortId) } };
  };
  const loadClients = async (kind: 'active' | 'archived') => {
    if (!isCurrent() || !cohortId || !currentUser || (kind === 'archived' && !enabled)) return;
    controllers.current[kind]?.abort();
    const controller = new AbortController();
    controllers.current[kind] = controller;
    const sequence = ++requests.current[kind];
    const update = (value: ReturnType<typeof emptyList>) => {
      if (isCurrent() && requests.current[kind] === sequence) {
        setLists(previous => ({ ...(previous.scope === scope ? previous : { scope, active: emptyList(), archived: emptyList() }), [kind]: value }));
      }
    };
    update({ data: [], loading: true, error: null });
    try {
      const { data } = await axiosInstance.get(kind === 'archived' ? '/clients/?archived=true' : '/clients/', {
        ...requestConfig(), signal: controller.signal,
      });
      if (!Array.isArray(data?.clients)) throw new Error('Invalid client response');
      update({ data: data.clients, loading: false, error: null });
    } catch (error) {
      if (!axios.isCancel(error) && !controller.signal.aborted) {
        update({ data: [], loading: false, error: clientErrorMessage(error, 'Unable to load clients. Please retry.') });
      }
    }
  }

  useEffect(() => {
    mounted.current = true;
    setAddClientError(null);
    setRemoveClientError(null);
    void loadClients('active');
    void loadClients('archived');
    return () => {
      mounted.current = false;
      controllers.current.active?.abort();
      controllers.current.archived?.abort();
      requests.current.active++;
      requests.current.archived++;
    };
  }, [scopeKey, axiosInstance, waiverResults]);

  const refreshClients = () => loadClients('active');
  const refreshArchivedClients = () => loadClients('archived');
  const reloadRef = useRef<() => Promise<void>>(async () => {});
  reloadRef.current = async () => { await Promise.all([refreshClients(), refreshArchivedClients()]); };
  const refreshAfterMutation = async () => {
    if (mounted.current && scopeRef.current.key === scopeKey) await reloadRef.current();
  };

  const addClient = async ({
    untracked,
    email,
    firstName,
    lastName,
    ageRange,
    phoneNumber,
    possiblyPregnant,
    hbpDiagnosed,
  }: {
    untracked?: boolean;
    email?: string;
    firstName?: string;
    lastName?: string;
    ageRange: string;
    phoneNumber: string;
    possiblyPregnant: string;
    hbpDiagnosed: string;
  }) => {
    let body = {}
    if (untracked) {
      body = {
        untracked_client: true,
        client_first_name: firstName,
        client_last_name: lastName,
        age_range: ageRange,
        phone_number: phoneNumber,
        possibly_pregnant: possiblyPregnant,
        hb_pressure_diagnosed: hbpDiagnosed
      }
    } else {
      body = {
        untracked_client: false,
        client_email: email,
      }
    }
    if (isCurrent()) setAddClientError(null);
    try {
      const { data } = await axiosInstance.put('/client/', JSON.stringify(body), requestConfig());
      if (!Array.isArray(data?.clients)) throw new Error('Invalid client response');
      await refreshAfterMutation();
    } catch (error) {
      if (isCurrent()) setAddClientError(clientErrorMessage(error, 'Unable to confirm the client was added. Refresh before retrying.'));
      throw error;
    }
  };

  const getClient = (client: User | number) => {
    const allClients = [...visible.active.data, ...visible.archived.data];
    if (client) {
      if (typeof client !== 'number') {
        return allClients.find((c: User) => c.id === client.id);
      } else {
        return allClients.find((c: User) => c.id === client);
      }
    }
  };

  const getClientRemovalPreview = async (clientId: number, expectedCohortId = cohortId) => {
    const { data } = await axiosInstance.get(
      `/client_removal_preview/?client=${clientId}`, requestConfig(expectedCohortId)
    );
    return data as ClientRemovalResult;
  }

  const removeClient = async (clientId: number, expectedCohortId = cohortId) => {
    try {
      const { data } = await axiosInstance.post(
        '/remove_client/',
        JSON.stringify({ client_id: clientId }), requestConfig(expectedCohortId)
      );
      if (data.action === 'archive') await refreshAfterMutation();
      return data as ClientRemovalResult;
    } catch (error) {
      if (isCurrent()) setRemoveClientError(clientErrorMessage(error, 'Unable to archive client.'));
      throw error;
    }
  }

  const restoreClient = async (clientId: number, expectedCohortId = cohortId) => {
    try {
      const { data } = await axiosInstance.post(
        '/restore_client/',
        JSON.stringify({ client_id: clientId }), requestConfig(expectedCohortId)
      );
      if (data?.action !== 'restored') throw new Error('Client was not restored');
      await refreshAfterMutation();
    } catch (error) {
      if (isCurrent()) setRemoveClientError(clientErrorMessage(error, 'Unable to restore client.'));
      throw error;
    }
  }

  const resendWaiver = async (caseId: number) => {
    try {
      await axiosInstance.post('/resend_case_waiver/', JSON.stringify({ case_id: caseId }), requestConfig());
      return true;
    } catch (e) {
      return false;
    }
  }

  const values = {
    scopeKey,
    cohortId,
    clients: visible.active.data,
    archivedClients: visible.archived.data,
    clientsError: visible.active.error,
    archivedClientsError: visible.archived.error,
    clientsLoading: visible.active.loading,
    archivedClientsLoading: visible.archived.loading,
    addClient,
    addClientError,
    setAddClientError,
    removeClient,
    removeClientError,
    setRemoveClientError,
    getClient,
    refreshClients,
    refreshArchivedClients,
    resendWaiver,
    getClientRemovalPreview,
    restoreClient,
  };

  return <ClientsContext.Provider value={values}>{children}</ClientsContext.Provider>;
}

